// src/components/layouts/ClipAudioSequences.tsx
import React from 'react';
import { Sequence, Audio, useRemotionEnvironment } from 'remotion';
import { buildSceneLayout, type CutWindow } from '@/lib/transitions/buildSceneLayout';
import type { SceneTransition } from '@/lib/transitions/types';
import { sceneAudioFades, makeSceneVolume } from '@/lib/transitions/resolveSceneVolume';

export interface ClipInterval { startTime: number; endTime: number; }
export interface FrameSequence { from: number; startFrom: number; endAt: number; }

export const ClipAudioSequences: React.FC<{
  videoUrl: string;
  fps: number;
  intervals?: ClipInterval[];
  frameSequences?: FrameSequence[];
  isMuted?: boolean;
  volume?: number;
  /** Per-cut scene transitions, used in `intervals` mode: placement is
   *  recomputed via buildSceneLayout (same overlap-aware offsets as
   *  ClipVideoSequences) so overlap-kind transitions actually overlap the
   *  audio instead of drifting out of sync with the video track. */
  transitions?: SceneTransition[];
  /** Cut windows for `frameSequences` mode, derived by the host via
   *  cutsFromClipIntervals (the backend already placed the sequences).
   *  Drives the same audio crossfade ramps as ClipVideoSequences — no
   *  visual wrapper needed here, audio only. */
  cuts?: CutWindow[];
}> = ({ videoUrl, fps, intervals = [], frameSequences, isMuted = false, volume = 1, transitions = [], cuts = [] }) => {
  const baseVolume = isMuted ? 0 : volume;
  // @remotion/web-renderer (the in-browser cover still) throws on the HTML5
  // <Audio>, and a still has no sound anyway.
  const { isClientSideRendering } = useRemotionEnvironment();
  if (isClientSideRendering) return null;

  if (frameSequences) {
    return (
      <>
        {frameSequences.map((seq, i) => {
          const dur = seq.endAt - seq.startFrom;
          if (dur <= 0) return null;
          const fades = sceneAudioFades(cuts, i);
          const vol = makeSceneVolume({
            baseVolume,
            fadeInFrames: fades.fadeInFrames,
            fadeOutFrames: fades.fadeOutFrames,
            durationInFrames: dur,
          });
          return (
            <Sequence key={i} from={seq.from} durationInFrames={dur} premountFor={150}>
              <Audio src={videoUrl} startFrom={seq.startFrom} endAt={seq.endAt} volume={vol} pauseWhenBuffering />
            </Sequence>
          );
        })}
      </>
    );
  }

  const layout = buildSceneLayout({ scenes: intervals, transitions, fps });
  return (
    <>
      {layout.blocks.map((block, i) => {
        if (block.durationInFrames <= 0) return null;
        const fades = sceneAudioFades(layout.cuts, i);
        const vol = makeSceneVolume({
          baseVolume,
          fadeInFrames: fades.fadeInFrames,
          fadeOutFrames: fades.fadeOutFrames,
          durationInFrames: block.durationInFrames,
        });
        return (
          <Sequence key={i} from={block.from} durationInFrames={block.durationInFrames} premountFor={150}>
            <Audio src={videoUrl} startFrom={block.trimBefore} endAt={block.trimAfter} volume={vol} pauseWhenBuffering />
          </Sequence>
        );
      })}
    </>
  );
};
